import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  orderBy,
  doc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { CourseContent, CourseGeneratorFormValues, CourseState, TodoItem } from '../types';

export interface StoredCourse {
  id: string;
  userId: string;
  input: CourseGeneratorFormValues;
  content: CourseContent;
  createdAt: Date;
  state: CourseState;
  startedAt?: Date;
}

export const courseService = {
  // Save a new course
  async saveCourse(
    userId: string, 
    input: CourseGeneratorFormValues, 
    content: CourseContent
  ): Promise<string> {
    try {
      const courseData = {
        userId,
        input,
        content: {
          ...content,
          state: 'not_started' as CourseState
        },
        createdAt: new Date(),
        state: 'not_started' as CourseState
      };

      const docRef = await addDoc(collection(db, 'courses'), courseData);
      return docRef.id;
    } catch (error) {
      console.error('Error saving course:', error);
      throw new Error('Failed to save course');
    }
  },

  // Get all courses for a user
  async getUserCourses(userId: string): Promise<StoredCourse[]> {
    try {
      const coursesRef = collection(db, 'courses');
      const q = query(
        coursesRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate(),
        startedAt: doc.data().startedAt?.toDate(),
      })) as StoredCourse[];
    } catch (error) {
      console.error('Error getting user courses:', error);
      throw new Error('Failed to get user courses');
    }
  },

  // Update course state
  async updateCourseState(
    courseId: string,
    state: CourseState,
    startedAt?: Date
  ): Promise<void> {
    try {
      const courseRef = doc(db, 'courses', courseId);
      
      const updateData: any = { 
        state,
        'content.state': state
      };
      
      if (startedAt) {
        updateData.startedAt = startedAt;
      }
      
      await updateDoc(courseRef, updateData);
    } catch (error) {
      console.error('Error updating course state:', error);
      throw new Error('Failed to update course state');
    }
  },

  // Update all courses without a state to 'not_started'
  async updateLegacyCourses(userId: string): Promise<void> {
    try {
      const coursesRef = collection(db, 'courses');
      const q = query(
        coursesRef,
        where('userId', '==', userId)
      );

      const querySnapshot = await getDocs(q);
      const batch = writeBatch(db);
      let updateCount = 0;

      querySnapshot.forEach((docSnapshot) => {
        const courseData = docSnapshot.data();
        if (!courseData.state) {
          const docRef = doc(db, 'courses', docSnapshot.id);
          batch.update(docRef, { 
            state: 'not_started',
            'content.state': 'not_started'
          });
          updateCount++;
        }
      });

      if (updateCount > 0) {
        await batch.commit();
        console.log(`Updated ${updateCount} courses to 'not_started' state`);
      }
    } catch (error) {
      console.error('Error updating legacy courses:', error);
      throw new Error('Failed to update legacy courses');
    }
  },

  // Update todos for a course
  async updateTodos(courseId: string, todos: TodoItem[]): Promise<void> {
    try {
      const courseRef = doc(db, 'courses', courseId);
      await updateDoc(courseRef, {
        'content.todos': todos
      });
    } catch (error) {
      console.error('Error updating todos:', error);
      throw new Error('Failed to update todos');
    }
  },

  // Initialize todos for a course if they don't exist
  async initializeTodos(courseId: string, todos: TodoItem[]): Promise<void> {
    try {
      const courseRef = doc(db, 'courses', courseId);
      await updateDoc(courseRef, {
        'content.todos': todos
      });
    } catch (error) {
      console.error('Error initializing todos:', error);
      throw new Error('Failed to initialize todos');
    }
  }
}; 